import { ChangeDetectionStrategy, Component, inject, input, OnInit, output, signal } from '@angular/core';
import { MatDialog } from '@angular/material/dialog';
import { firstValueFrom } from 'rxjs';

import { FieldInterventionFormEntityControlResultsContentComponent } from '@connect-services/client/components/field-intervention-form-entity-control-results/field-intervention-form-entity-control-results-content/field-intervention-form-entity-control-results-content.component';
import { FieldInterventionFormEntityControlResultsDetailComponent } from '@connect-services/client/components/field-intervention-form-entity-control-results/field-intervention-form-entity-control-results-detail/field-intervention-form-entity-control-results-detail.component';
import { IconButtonComponent } from '@connect-services/client/components/icon-button/icon-button.component';
import { FieldInterventionControlResultCommentModalComponent } from '@connect-services/client/components/modals/field-intervention/field-intervention-control-result-comment-modal/field-intervention-control-result-comment-modal.component';
import { FieldInterventionControlResultDetails } from '@connect-services/client/services/field-intervention.service';
import { FieldInterventionControlResult, FieldInterventionControlResultsService } from '@connect-services/src/sdk/api';
import { RessourcesModule } from '@ressources/client/ressources.module';
import { FormService } from '@shared/client/services/form.service';
import { SnackBarService } from '@shared/client/services/snack-bar.service';

interface ControlResultUpdatePayload {
    comment?: string;
    statusUpdatedAt?: string;
    validation?: FieldInterventionControlResult.ValidationEnum;
}

export const FIELD_INTERVENTION_CONTROL_RESULTS_COLUMN_WIDTHS = {
    actions: 'w-[20%]',
    controlPoint: 'w-[30%]',
    result: 'w-[30%]',
    status: 'w-[20%]',
};

@Component({
    changeDetection: ChangeDetectionStrategy.OnPush,
    imports: [
        FieldInterventionFormEntityControlResultsContentComponent,
        FieldInterventionFormEntityControlResultsDetailComponent,
        IconButtonComponent,
        RessourcesModule,
    ],
    selector: 'app-field-intervention-form-entity-control-results',
    styleUrl: './field-intervention-form-entity-control-results.component.scss',
    templateUrl: './field-intervention-form-entity-control-results.component.html',
})
export class FieldInterventionFormEntityControlResultsComponent implements OnInit {
    public readonly controlResult = input.required<FieldInterventionControlResultDetails>();
    public readonly controlResultUpdated = output<FieldInterventionControlResult>();
    public readonly initiallyExpanded = input<boolean>(false);
    public readonly isControlResultExpanded = signal<boolean>(false);
    protected readonly columnWidthClasses = FIELD_INTERVENTION_CONTROL_RESULTS_COLUMN_WIDTHS;

    private readonly fieldInterventionControlResultsService = inject(FieldInterventionControlResultsService);
    private readonly matDialog = inject(MatDialog);
    private readonly snackbarService = inject(SnackBarService);

    public ngOnInit(): void {
        this.isControlResultExpanded.set(this.initiallyExpanded());
    }

    protected async onValidityChanged(newStatus: FieldInterventionControlResult.ValidationEnum): Promise<void> {
        const updatePayload: ControlResultUpdatePayload = {
            statusUpdatedAt: new Date().toISOString(),
            validation: newStatus,
        };

        await this.updateValidation(updatePayload);
    }

    protected async openCommentDialog(event: MouseEvent): Promise<void> {
        event.stopPropagation();

        const dialogRef = this.matDialog.open(FieldInterventionControlResultCommentModalComponent, {
            data: {
                comment: this.controlResult().comment,
                maxLength: FormService.VARCHAR_COMMON_LENGTH,
            },
            width: '40rem',
        });

        const comment = await firstValueFrom(dialogRef.afterClosed());

        if (!comment) {
            return;
        }

        await this.updateValidation({ comment });
    }

    protected toggleResultDetail(event?: MouseEvent): void {
        event?.stopPropagation();

        this.isControlResultExpanded.update((isExpanded: boolean) => !isExpanded);
    }

    private async updateValidation(updatePayload: ControlResultUpdatePayload): Promise<void> {
        try {
            const updatedControlResult = await firstValueFrom(
                this.fieldInterventionControlResultsService.fieldInterventionControlResultControllerPatch({
                    comment: updatePayload.comment,
                    id: this.controlResult().id,
                    statusUpdatedAt: updatePayload.statusUpdatedAt,
                    validation: updatePayload.validation,
                }),
            );

            this.controlResultUpdated.emit(updatedControlResult);
            this.snackbarService.openSuccessSnackBar('formAnswerEditionSuccess');
        } catch {
            this.snackbarService.openErrorSnackBar('modificationsNotSaved');
        }
    }
}
